import {
    assistantAsking, assistantAskingForAction,
    assistantAskingForContext, assistantAskingForExample,
    assistantAskingForGenre, assistantAskingForInstructions, assistantAskingForRequirements, assistantAskingForSchema,
    assistantAskingForSynopsis, systemInstruction, userSaying
} from "../../../../../../../ai/Utils";
import {DraftChapter, DraftScene, DraftSection} from "../../../../../../../feature/Draft";


export const generateScenesPrompt = (vars: {
    section: DraftSection,
    prevSection?: DraftSection,
    nextSection?: DraftSection,
    genres: string[],
    synopsis: string,
    prevScenes?: { scene: number, content: string }[] | undefined,
    context?: string[]
}) => systemInstruction("Ты профессиональный писатель")
    .concat(userSaying("Я работаю над созданием литературного произведения"))
    .concat(assistantAskingForGenre(vars.genres))
    .concat(assistantAskingForSynopsis(vars.synopsis))
    .concat(assistantAsking("Опиши кратко секцию главы, над которой ведется работа", vars.section.description))
    .concat(assistantAsking("Опиши кратко предыдущую секцию главы", vars.prevSection?.description))
    .concat(assistantAsking("Опиши кратко следующую секцию главы", vars.nextSection?.description))
    .concat(assistantAsking("Опиши содержимое предыдущих сцен", vars.prevScenes ? JSON.stringify(vars.prevScenes) : undefined))
    .concat(assistantAskingForContext(vars.context))
    .concat(assistantAskingForInstructions([
        "Проанализируй содержимое предыдущих сцен",
        "Придумай продолжение согласно содержимому секции главы",
    ]))
    .concat(assistantAskingForRequirements([
        "Действия и события в сценах не должны повторяться",
        "Сцены должны быть связаны с содержимым секции главы",
        "Не придумывай ничего, что не описано в кратком содержании секции главы",
        "Не забегай вперед по сюжету, описывай только то событие, которое происходит в текущей секции главы",
        "Результат должен быть в формате json"
    ]))
    .concat(assistantAskingForSchema('{"data": [{"id":number, "title":string, "description":string}],}'))
    .concat(assistantAskingForExample({
            data: [
                {
                    id: 1,
                    title: "Находка",
                    description: "- В ночь на 31 декабря главный герой отправляется в поход и находит магический артефакт.\n\n- Он приносит артефакт домой и замечает изменения вокруг себя"
                }
            ]
        }
    ))
    .concat(assistantAskingForAction(`Сгенерируй сцены для секции главы, над которой ведется работа.`))

export const generateScenePrompt = (vars: {
    genres: string[],
    synopsis: string,
    scene: number,
    scenes: DraftScene[],
    context?: string[]
}) => systemInstruction("Ты профессиональный писатель")
    .concat(userSaying("Я работаю над созданием литературного произведения"))
    .concat(assistantAskingForGenre(vars.genres))
    .concat(assistantAskingForSynopsis(vars.synopsis))
    .concat(assistantAsking("Опиши все сцены секции", JSON.stringify(vars.scenes.map((item) => ({
        scene: item.id,
        content: item.description
    })))))
    .concat(assistantAskingForContext(vars.context))
    .concat(assistantAskingForInstructions([
        "Придумай содержимое сцены",
        "Проработай содержимое сцены"
    ]))
    .concat(assistantAskingForRequirements([
        "Объем текста должен оставаться таким же как в исходной сцене",
        "События не должны пересекаться с другими сценами",
        "Результат должен быть в формате json"
    ]))
    .concat(assistantAskingForSchema('{"data": {"id":number, "title":string, "description":string}'))
    .concat(assistantAskingForExample(
        {
            data: {
                id: 1,
                title: "Находка",
                description: "- В ночь на 31 декабря главный герой отправляется в поход и находит магический артефакт.\n\nОн приносит артефакт домой и замечает изменения вокруг себя."
            }
        }
    ))
    .concat(assistantAskingForAction(`Перегенерируй существующую или сгенерируй новую ${vars.scene}-ю сцену истории.`))


export const improveScenePrompt = (vars: {
    scene: DraftScene,
    genres: string[],
    synopsis: string,
    scenes: DraftScene[],
    context?: string[]
}) => systemInstruction("Ты профессиональный писатель")
    .concat(userSaying("Я работаю над созданием литературного произведения"))
    .concat(assistantAskingForGenre(vars.genres))
    .concat(assistantAskingForSynopsis(vars.synopsis))
    .concat(assistantAsking("Опиши все сцены секции", JSON.stringify(vars.scenes.map((item) => ({
        scene: item.id,
        content: item.description
    })))))
    .concat(assistantAskingForContext(vars.context))
    .concat(assistantAskingForInstructions([
        `Проанализируй краткое содержание ${vars.scene.id}-й сцены`,
        "Наполни сцену деталями, чтобы разнообразить сюжет",
        "Добавь действия и события, которые могут произойти в сцене",
        "Можно добавить вне сюжетные события, истории, рассказы, если это необходимо для раскрытия смысла или объяснения неочевидных моментов",
        "Убедись, что добавленные действия и события соответствуют содержимому сцены",
        "Убедись, что добавленные действия и события не забегают вперед по сюжету",
        "Разбей содержимое сцены на абзацы"
    ]))
    .concat(assistantAskingForRequirements([
        "Действия и события в сценах не должны повторяться",
        "Результат должен быть в формате json"
    ]))
    .concat(assistantAskingForSchema('{"data": {"id":number, "title":string, "description":string}'))
    .concat(assistantAskingForExample({
            data: {
                id: 1,
                title: "Осмотр места преступления",
                description: "Детектив наслаждается пейзажем рядом с местом преступления. После он отправляется на место преступления и разговаривает с криминалистами. Криминалисты рассказывают ему о найденных уликах. У детектива начинает складываться картина произошедшего. Он мысленно составляет психологический портрет преступника. Детектив возвращается в участок"
            }
        }
    ))
    .concat(assistantAskingForAction(`Наполни ${vars.scene.id}-ю сцену.`))